import React, { useState } from "react"
import {
  ShoppingCart,
  User,
  Menu,
  X,
  ChevronDown,
  LogOut,
} from "lucide-react"
import { Link } from "react-router-dom"
import { useCategories } from "@/hooks/useCategories"
import { useCart } from "@/hooks/useCart"
import { useLogout } from "@/hooks/useLogout"
import { useWebsiteConfig } from "@/hooks/useWebsiteConfig"

export const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories()
  const { cart } = useCart()
  const logoutMutation = useLogout()
  const { data: websiteConfig } = useWebsiteConfig()
  const token = localStorage.getItem("website_token")
  const customerStr = localStorage.getItem("customer")
  const customer = customerStr ? JSON.parse(customerStr) : null

  // ApiResponse wraps in 'data', and ResourceCollection also wraps in 'data'
  // So for collections: response.data.data
  const categories =
    (categoriesData as any)?.data?.data || (categoriesData as any)?.data || []
  const cartItemCount = cart?.items?.length || 0

  const handleLogout = () => {
    logoutMutation.mutate()
  }

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/website" className="text-2xl font-bold text-indigo-600 flex items-center h-full">
              {websiteConfig?.logo_image ? (
                <img 
                  src={websiteConfig.logo_image} 
                  alt={websiteConfig?.title || "Logo"} 
                  className="max-h-12 w-auto object-contain" 
                />
              ) : (
                "SHOP.CO"
              )}
            </Link>
          </div>

          {/* Desktop Menu with Hover Dropdown */}
          <div className="hidden md:flex space-x-8 items-center h-full">
            {categoriesLoading ? (
              <div className="text-gray-500">Loading...</div>
            ) : (
              categories.map((category: any) => (
                <div
                  key={category.id}
                  className="relative group h-full flex items-center"
                >
                  <Link
                    to={`/website/category/${category.id}`}
                    className="flex items-center text-gray-700 hover:text-indigo-600 font-medium transition-colors"
                  >
                    {category.name}
                    {category.tags && category.tags.length > 0 && (
                      <ChevronDown size={16} className="ml-1" />
                    )}
                  </Link>

                  {/* Dropdown Menu for Tags */}
                  {category.tags && category.tags.length > 0 && (
                    <div className="absolute top-full left-0 w-48 bg-white shadow-lg rounded-b-md py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 border-t-2 border-indigo-600 transform translate-y-2 group-hover:translate-y-0">
                      {category.tags.map((tag: any) => (
                        <Link
                          key={tag.id}
                          to={`/website/tag/${tag.id}`}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                        >
                          {tag.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Icons */}
          <div className="flex items-center space-x-6">
            {/* <Search className="w-6 h-6 text-gray-600 cursor-pointer hover:text-indigo-600" /> */}
            <Link to="/website/cart" className="relative">
              <ShoppingCart className="w-6 h-6 text-gray-600 hover:text-indigo-600" />
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Link>
            {token ? (
              <div className="flex items-center space-x-4">
                {customer?.name && (
                  <span className="text-sm text-gray-700">{customer.name}</span>
                )}
                <Link
                  to="/website/orders"
                  className="text-gray-700 hover:text-indigo-600"
                >
                  Orders
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center text-gray-700 hover:text-indigo-600"
                >
                  <LogOut size={18} className="mr-1" />
                  Logout
                </button>
              </div>
            ) : (
              <Link to="/website/login">
                <User className="w-6 h-6 text-gray-600 hover:text-indigo-600" />
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-gray-600"
            >
              {isOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu (Simple implementation) */}
      {isOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {categories.map((category: any) => (
              <div key={category.id}>
                <Link
                  to={`/website/category/${category.id}`}
                  className="block px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                >
                  {category.name}
                </Link>
                {category.tags && category.tags.length > 0 && (
                  <div className="pl-6">
                    {category.tags.map((tag: any) => (
                      <Link
                        key={tag.id}
                        to={`/website/tag/${tag.id}`}
                        className="block px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                      >
                        {tag.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  )
}
